/**
 * PipelineStageRow — one presentational row of the vertical stage timeline:
 * rail node + connector on the left; title, StatusPill, description, meta,
 * action button(s), inline error banner, and expandable content on the right.
 */

import React, { useState } from 'react';
import { StatusPill } from '../../ui';
import { PipelineStage } from '../../../services/adminApi';
import { StagePill } from './pipelineUtils';

interface PipelineStageRowProps {
  /** 1-based step number shown in the rail node while the stage is pending */
  index: number;
  title: string;
  description?: string;
  stage?: PipelineStage;
  pill: StagePill;
  /** Who/when line under the title (e.g. "Confirmed by james@… · Jul 6") */
  meta?: string;
  /** Soft advisory caption (12.5px, fg-3) */
  nudge?: string;
  /** Right-aligned action button(s) */
  action?: React.ReactNode;
  /** Persistent error banner (DS §4.6) */
  error?: string;
  children?: React.ReactNode;
  isLast?: boolean;
}

function nodeAppearance(stage: PipelineStage | undefined): {
  border: string;
  background: string;
  color: string;
  glyph: 'check' | 'cross' | 'dot' | 'number';
} {
  const status = stage?.status ?? 'pending';
  switch (status) {
    case 'completed':
      return { border: 'var(--success)', background: 'var(--success)', color: '#fff', glyph: 'check' };
    case 'confirmed':
      return { border: 'var(--info)', background: 'var(--info)', color: '#fff', glyph: 'check' };
    case 'running':
      return { border: 'var(--success)', background: 'var(--bg-surface-1)', color: 'var(--success)', glyph: 'dot' };
    case 'failed':
      return { border: 'var(--danger)', background: 'var(--danger-soft)', color: 'var(--danger)', glyph: 'cross' };
    case 'skipped':
      return { border: 'var(--border-2)', background: 'var(--bg-surface-3)', color: 'var(--fg-4)', glyph: 'number' };
    default:
      return { border: 'var(--border-2)', background: 'var(--bg-surface-1)', color: 'var(--fg-4)', glyph: 'number' };
  }
}

const PipelineStageRow: React.FC<PipelineStageRowProps> = ({
  index,
  title,
  description,
  stage,
  pill,
  meta,
  nudge,
  action,
  error,
  children,
  isLast,
}) => {
  const node = nodeAppearance(stage);
  const done = stage?.status === 'completed' || stage?.status === 'confirmed';

  return (
    <div style={styles.row}>
      {/* Rail: node + connector */}
      <div style={styles.rail}>
        <div
          style={{
            ...styles.node,
            borderColor: node.border,
            background: node.background,
            color: node.color,
          }}
          aria-hidden
        >
          {node.glyph === 'check' && '✓'}
          {node.glyph === 'cross' && '✗'}
          {node.glyph === 'dot' && <span style={styles.pulsingDot} />}
          {node.glyph === 'number' && index}
        </div>
        {!isLast && (
          <div
            style={{
              ...styles.connector,
              background: done ? 'var(--success)' : 'var(--border-1)',
            }}
          />
        )}
      </div>

      {/* Content */}
      <div style={styles.content}>
        <div style={styles.headerRow}>
          <div style={styles.titleWrap}>
            <span style={styles.title}>{title}</span>
            <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
          </div>
          {action && <div style={styles.actions}>{action}</div>}
        </div>
        {description && <div style={styles.description}>{description}</div>}
        {meta && <div style={styles.meta}>{meta}</div>}
        {nudge && <div style={styles.nudge}>{nudge}</div>}
        {error && (
          <div style={styles.errorBanner} role="alert">
            {error}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

/**
 * Disclosure — a chevron summary that expands inline content (native <details>
 * resets its open state on re-render when controlled; this keeps local state).
 */
export const Disclosure: React.FC<{
  summary: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ summary, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={styles.disclosure}>
      <button type="button" style={styles.disclosureSummary} onClick={() => setOpen((v) => !v)}>
        <span style={{ ...styles.disclosureChevron, transform: open ? 'rotate(90deg)' : 'none' }}>
          {'›'}
        </span>
        {summary}
      </button>
      {open && <div style={styles.disclosureBody}>{children}</div>}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  row: {
    display: 'grid',
    gridTemplateColumns: '40px 1fr',
    gap: 0,
  },
  rail: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    paddingTop: 2,
  },
  node: {
    width: 28,
    height: 28,
    borderRadius: 'var(--radius-pill)',
    border: '2px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    fontFamily: 'var(--font-sans)',
    flexShrink: 0,
    transition: 'all var(--dur-fast) var(--ease-standard)',
  },
  pulsingDot: {
    width: 8,
    height: 8,
    borderRadius: 'var(--radius-pill)',
    background: 'var(--success)',
    display: 'inline-block',
    animation: 'cgPulse 1.5s ease-in-out infinite',
  },
  connector: {
    width: 2,
    flex: 1,
    minHeight: 16,
    borderRadius: 1,
  },
  content: {
    paddingLeft: 12,
    paddingBottom: 24,
    minWidth: 0,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    minHeight: 28,
  },
  titleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  description: {
    fontSize: 12.5,
    color: 'var(--fg-3)',
    marginTop: 4,
    lineHeight: 1.5,
    maxWidth: 560,
  },
  meta: {
    fontSize: 12,
    color: 'var(--fg-3)',
    marginTop: 6,
  },
  nudge: {
    fontSize: 12.5,
    color: 'var(--fg-3)',
    marginTop: 6,
    fontStyle: 'italic',
  },
  errorBanner: {
    marginTop: 8,
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--danger)',
    background: 'var(--danger-soft)',
    border: '1px solid var(--cg-terra-200)',
    borderRadius: 'var(--radius-md)',
    lineHeight: 1.45,
  },
  disclosure: {
    marginTop: 10,
  },
  disclosureSummary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    padding: 0,
    fontSize: 12.5,
    fontWeight: 600,
    color: 'var(--fg-2)',
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    boxShadow: 'none',
  },
  disclosureChevron: {
    display: 'inline-block',
    transition: 'transform var(--dur-fast) var(--ease-standard)',
    fontSize: 14,
    lineHeight: 1,
  },
  disclosureBody: {
    marginTop: 8,
  },
};

export default PipelineStageRow;
